import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { authApi } from '../services/api';

const AuthContext = createContext(null);

// Refresh every 10 minutes while the user is active (office tokens last 60 min).
const TOKEN_REFRESH_INTERVAL = 10 * 60 * 1000;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('equipr_token'));
  const refreshIntervalRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // Track user activity
  const updateLastActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  // Proactive token refresh
  const refreshTokenSilently = useCallback(async () => {
    // Only refresh if user has been active in the last 60 minutes
    const inactivityTime = Date.now() - lastActivityRef.current;
    const maxInactivityTime = 60 * 60 * 1000;
    
    if (inactivityTime > maxInactivityTime) {
      console.log('User inactive, skipping token refresh');
      return;
    }

    try {
      const response = await authApi.refreshToken();
      const { token: newToken, user: userData } = response.data;
      
      localStorage.setItem('equipr_token', newToken);
      localStorage.setItem('equipr_user', JSON.stringify(userData));
      setToken(newToken);
      setUser(userData);
      console.log('Token refreshed silently');
    } catch (error) {
      console.error('Silent token refresh failed:', error);
      // Don't logout on silent refresh failure - let the interceptor handle it
    }
  }, []);

  // Set up activity listeners and refresh interval
  useEffect(() => {
    if (token && user) {
      // Track user activity
      const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart'];
      activityEvents.forEach(event => {
        window.addEventListener(event, updateLastActivity, { passive: true });
      });

      // Start refresh interval
      refreshIntervalRef.current = setInterval(refreshTokenSilently, TOKEN_REFRESH_INTERVAL);

      return () => {
        // Cleanup
        activityEvents.forEach(event => {
          window.removeEventListener(event, updateLastActivity);
        });
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
        }
      };
    }
  }, [token, user, refreshTokenSilently, updateLastActivity]);

  useEffect(() => {
    const initAuth = async () => {
      const savedToken = localStorage.getItem('equipr_token');
      const savedUser = localStorage.getItem('equipr_user');
      
      if (savedToken && savedUser) {
        try {
          setToken(savedToken);
          setUser(JSON.parse(savedUser));
          // Verify token is still valid and refresh it
          const response = await authApi.refreshToken();
          const { token: newToken, user: userData } = response.data;
          
          localStorage.setItem('equipr_token', newToken);
          localStorage.setItem('equipr_user', JSON.stringify(userData));
          setToken(newToken);
          setUser(userData);
        } catch (error) {
          console.error('Auth verification failed:', error);
          logout();
        }
      }
      setLoading(false);
    };
    
    initAuth();
  }, []);

  /**
   * Login. clientId is optional — pass it for backwards-compat or when the
   * user explicitly typed one; otherwise pass null/undefined and the server
   * will resolve the tenant from the email.
   */
  const login = async (emailOrClientId, emailOrPassword, password) => {
    // Support BOTH legacy 3-arg `login(clientId, email, password)` callers
    // AND the new 2-arg `login(email, password)` shape.
    let payload;
    if (password === undefined) {
      // 2-arg shape: (email, password)
      payload = { email: emailOrClientId, password: emailOrPassword };
    } else {
      // 3-arg shape: (clientId, email, password)
      payload = { client_id: emailOrClientId || null, email: emailOrPassword, password };
    }
    const response = await authApi.login(payload);
    const { token: newToken, user: userData } = response.data;

    localStorage.setItem('equipr_token', newToken);
    localStorage.setItem('equipr_user', JSON.stringify(userData));
    setToken(newToken);
    setUser(userData);
    lastActivityRef.current = Date.now(); // Reset activity on login

    return userData;
  };

  const register = async (data) => {
    const response = await authApi.register(data);
    const { token: newToken, user: userData } = response.data;
    
    localStorage.setItem('equipr_token', newToken);
    localStorage.setItem('equipr_user', JSON.stringify(userData));
    setToken(newToken);
    setUser(userData);
    lastActivityRef.current = Date.now(); // Reset activity on register
    
    return userData;
  };

  /**
   * Set an authenticated session from a pre-issued token + user (e.g. the
   * Test Drive frictionless signup returns these directly from the server).
   * Updates AuthContext state AND localStorage so the very next
   * ProtectedRoute render sees the user as authenticated.
   */
  const setSession = (newToken, userData) => {
    localStorage.setItem('equipr_token', newToken);
    localStorage.setItem('equipr_user', JSON.stringify(userData));
    setToken(newToken);
    setUser(userData);
    lastActivityRef.current = Date.now();
  };

  const logout = () => {
    localStorage.removeItem('equipr_token');
    localStorage.removeItem('equipr_user');
    localStorage.removeItem('equipr_last_activity');
    setToken(null);
    setUser(null);
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }
  };

  const isAdmin = () => user?.role === 'admin';
  const isFleetManager = () => user?.role === 'fleet_manager';
  const isMechanic = () => user?.role === 'mechanic';
  // Office users (paid seats) have access to back-office screens.
  const isOfficeUser = () => user?.role === 'admin' || user?.role === 'fleet_manager';
  // Back-compat alias — anything that used to be 'operator' is now 'mechanic'.
  const isOperator = () => user?.role === 'mechanic';

  const refreshNow = refreshTokenSilently;

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      register,
      logout,
      refreshNow,
      isAdmin,
      isFleetManager,
      isOfficeUser,
      isOperator,
      isMechanic,
      setSession,
      isAuthenticated: !!token,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
